import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";
import { LegalBody, LegalSection } from "@/components/LegalLayout";
import { canonicalFor } from "@/lib/site";

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        title="Privacy Policy | Crestline Roofing Ltd."
        description="How Crestline Roofing Ltd. collects, uses, and protects your information. Read our privacy policy or contact us at (778) 917-6817 to request data deletion."
        canonical={canonicalFor("/privacy-policy")}
      />
      <PageLayout>
        {() => (
          <main data-testid="privacy-page" className="bg-[var(--crest-charcoal)] text-white">
            <PageHero
              eyebrow="Legal"
              title="Privacy Policy"
              subtitle="How Crestline Roofing Ltd. collects, uses, and protects the information you share with us through this website and our estimate process."
              image="https://images.unsplash.com/photo-1601658797866-7ae55d05a817?crop=entropy&cs=srgb&fm=webp&q=85&w=2400"
              imageAlt="Residential rooftops across the Lower Mainland — Crestline Roofing Ltd. Vancouver"
              breadcrumb={[{ label: "Home", href: "/" }, { label: "Privacy Policy" }]}
            />

            <LegalBody updated="June 2026">
              <LegalSection heading="What information we collect">
                <p>
                  When you request an estimate, get in touch, or otherwise
                  interact with this website, Crestline Roofing Ltd. may
                  collect the following information:
                </p>
                <ul>
                  <li><strong>Name</strong> — so we know who we&apos;re speaking with.</li>
                  <li><strong>Email address</strong> — so we can send your quote and follow up.</li>
                  <li><strong>Phone number</strong> — so we can schedule a site visit or call you back.</li>
                  <li><strong>Property address</strong> — so we can prepare an accurate estimate and route a crew.</li>
                  <li><strong>Project details you provide</strong> — service type, roof age, leak description, photos, insurance information, etc.</li>
                </ul>
                <p>
                  We collect this information only when you choose to give it to
                  us — typically through our estimate form, a phone call, or an
                  email exchange.
                </p>
              </LegalSection>

              <LegalSection heading="How we use your information">
                <p>
                  We use the information you share with us for a single
                  purpose: to provide and improve our roofing services to you.
                  Specifically, we use it to:
                </p>
                <ul>
                  <li>Contact you about your roofing project, estimate, or inquiry.</li>
                  <li>Schedule site visits, inspections, repairs, and installations.</li>
                  <li>Send written quotes, proposals, scope-of-work documents, and warranty paperwork.</li>
                  <li>Follow up on completed work for warranty, quality assurance, or seasonal maintenance reminders.</li>
                  <li>Respond to questions, concerns, or warranty claims.</li>
                </ul>
                <p>
                  <strong>
                    We never sell, rent, or share your personal information
                    with third parties for marketing purposes.
                  </strong>{" "}
                  The only third parties who may briefly handle your
                  information are the trusted service providers we use to run
                  our business — for example, our email service, our scheduling
                  software, or our payment processor — and only to the extent
                  necessary to deliver our services to you.
                </p>
              </LegalSection>

              <LegalSection heading="Cookies and analytics">
                <p>
                  This website uses{" "}
                  <strong>Google Analytics 4 (GA4)</strong> to understand how
                  visitors find and use our site so we can keep improving it.
                  GA4 sets first-party cookies and collects information such as:
                </p>
                <ul>
                  <li>The pages you view and how long you stay on each one.</li>
                  <li>The general geographic region you&apos;re visiting from (city / region level, not your exact address).</li>
                  <li>The type of device and browser you&apos;re using.</li>
                  <li>How you arrived at our site (search, referral, direct).</li>
                </ul>
                <p>
                  This information is aggregated and used in a way that does
                  not personally identify you. You can opt out of Google
                  Analytics at any time by installing the{" "}
                  <a
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google Analytics Opt-out Browser Add-on
                  </a>{" "}
                  or by clearing / blocking cookies in your browser settings.
                </p>
                <p>
                  We do not use cookies for advertising retargeting or
                  third-party ad networks.
                </p>
              </LegalSection>

              <LegalSection heading="How long we keep your information">
                <p>
                  We retain your information only as long as needed to deliver
                  the service you requested and to meet our warranty and
                  insurance obligations. Project records (including photos,
                  scopes of work, and warranty documents) are typically kept
                  for the full length of the workmanship warranty plus seven
                  years for tax and insurance purposes. After that, records are
                  securely destroyed.
                </p>
              </LegalSection>

              <LegalSection heading="How to request data deletion">
                <p>
                  You have the right to access, correct, or request deletion
                  of your personal information at any time. To make a request,
                  contact us by phone or email and we&apos;ll respond within 30
                  days:
                </p>
                <ul>
                  <li>
                    Phone:{" "}
                    <a href="tel:+17789176817">(778) 917-6817</a>
                  </li>
                  <li>
                    Email:{" "}
                    <a href="mailto:info@crestlineroofing.ca">
                      info@crestlineroofing.ca
                    </a>
                  </li>
                </ul>
                <p>
                  Please note we may need to retain certain information to
                  satisfy legal, tax, warranty, or insurance obligations even
                  after you request deletion.
                </p>
              </LegalSection>

              <LegalSection heading="Changes to this policy">
                <p>
                  We may update this Privacy Policy from time to time. Any
                  changes will be posted on this page with a revised &ldquo;Last
                  updated&rdquo; date. Material changes will be communicated
                  more directly when reasonably possible.
                </p>
              </LegalSection>

              <LegalSection heading="Contact">
                <p>
                  Questions about this policy or how we handle your information?
                  Reach out:
                </p>
                <ul>
                  <li><a href="tel:+17789176817">(778) 917-6817</a></li>
                  <li><a href="mailto:info@crestlineroofing.ca">info@crestlineroofing.ca</a></li>
                  <li>Crestline Roofing Ltd. — Lower Mainland, British Columbia</li>
                </ul>
              </LegalSection>
            </LegalBody>
          </main>
        )}
      </PageLayout>
    </>
  );
}
